import React, { useEffect, useMemo, useState } from "react"
import Popup from "../popup/popup"
import Select from "../select/select"
import DatePicker from "../datePicker/datePicker"
import styles from "./DuplicateQuotationModal.module.scss"
import { agent } from "../../apiClient/api"
import {
  QuotationAgencyAgentListAgencyModel,
  QuotationDuplicateRequestModel,
} from "../../models/axiosModel"

export interface DuplicateQuotationSourceData {
  agency_id?: string | null
  agent_id?: string | null
  number_of_adults?: number | null
  number_of_children?: number | null
  foc_pax_number?: number | null
  start_date?: string | null
  number_of_nights?: number | null
  flag_with_tip?: boolean | null
  flag_all_days_have_dinner?: boolean | null
  quotation_status_id: number;
}

/** Keep missing/unknown as `null` so the source card can stay empty. */
export const parseOptionalBoolean = (value: unknown): boolean | null =>
  typeof value === "boolean" ? value : null

export const readDuplicateSourceMealGuideFlags = (detailsData?: any, quotation?: any) => {
  const q = quotation ?? detailsData?.quotation
  const journey = detailsData?.journey_details
  return {
    flag_with_tip: parseOptionalBoolean(q?.flag_with_tip ?? detailsData?.flag_with_tip ?? journey?.flag_with_tip),
    flag_all_days_have_dinner: parseOptionalBoolean(
      q?.flag_all_days_have_dinner ?? detailsData?.flag_all_days_have_dinner ?? journey?.flag_all_days_have_dinner,
    ),
  }
}

interface DuplicateQuotationModalProps {
  isOpen: boolean
  onClose: () => void
  source: DuplicateQuotationSourceData | null
  isSubmitting?: boolean
  onConfirm: (payload: QuotationDuplicateRequestModel) => Promise<void>
}

const GUIDE_TIP_REQUIRED_ERROR = "Please select with Tip or without Tip"

const DuplicateQuotationModal: React.FC<DuplicateQuotationModalProps> = ({
  isOpen,
  onClose,
  source,
  isSubmitting = false,
  onConfirm,
}) => {
  const [agencyOptions, setAgencyOptions] = useState<QuotationAgencyAgentListAgencyModel[]>([])
  const [agencyId, setAgencyId] = useState("")
  const [agentId, setAgentId] = useState("")
  const [keepSameDates, setKeepSameDates] = useState(true)
  const [startDate, setStartDate] = useState("")
  const [adultCount, setAdultCount] = useState("0")
  const [childrenCount, setChildrenCount] = useState("0")
  const [focCount, setFocCount] = useState("0")
  const [errorText, setErrorText] = useState("")
  const [changeValues, setChangeValues] = useState(false)
  /** `null` = no radio selected yet in change mode. */
  const [guideWithTip, setGuideWithTip] = useState<boolean | null>(null)
  const [dinnerMealOn, setDinnerMealOn] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    let mounted = true
    const fetchAgencies = async () => {
      const res = await agent.QuotationAgencyAgentListAPI.get(true)
      if (!mounted) return
      setAgencyOptions(Array.isArray(res?.data) ? res.data : [])
    }
    fetchAgencies()
    return () => {
      mounted = false
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    setAgencyId(String(source?.agency_id || ""))
    setAgentId(String(source?.agent_id || ""))
    setKeepSameDates(true)
    setStartDate(String(source?.start_date || "").slice(0, 10))
    setAdultCount(String(Math.max(0, Number(source?.number_of_adults || 0))))
    setChildrenCount(String(Math.max(0, Number(source?.number_of_children || 0))))
    setFocCount(String(Math.max(0, Number(source?.foc_pax_number || 0))))
    setChangeValues(false)
    setGuideWithTip(null)
    setDinnerMealOn(false)
    setErrorText("")
  }, [isOpen, source])

  const selectedAgency = useMemo(
    () => agencyOptions.find((a) => String(a.agency_id || "") === agencyId),
    [agencyOptions, agencyId],
  )

  useEffect(() => {
    if (!isOpen) return
    if (agencyId) return
    if (agencyOptions.length === 0) return
    // Auto-select first agency if source does not provide a valid one.
    setAgencyId(String(agencyOptions[0]?.agency_id || ""))
  }, [isOpen, agencyId, agencyOptions])

  const agentSelectOptions = useMemo(
    () =>
      ((selectedAgency?.agents || []) as any[])
        .filter((agent) => agent?.agent_id && agent?.agent_name)
        .map((agent) => ({
          label: String(agent.agent_name || ""),
          value: String(agent.agent_id || ""),
        })),
    [selectedAgency],
  )

  const agencySelectOptions = useMemo(
    () =>
      agencyOptions.map((agency) => ({
        label: String(agency.agency_name || ""),
        value: String(agency.agency_id || ""),
      })),
    [agencyOptions],
  )

  const tripDurationLabel = useMemo(() => {
    const nights = Math.max(0, Number(source?.number_of_nights || 0))
    return `${nights} Nights / ${nights + 1} Days`
  }, [source?.number_of_nights])

  const sourceDinnerOn = source?.flag_all_days_have_dinner === true
  const sourceGuideWithTip = typeof source?.flag_with_tip === "boolean" ? source.flag_with_tip : null
  const displayedDinnerOn = changeValues ? dinnerMealOn : sourceDinnerOn
  const displayedGuideWithTip = changeValues ? guideWithTip : sourceGuideWithTip

  const resetMealGuideEdits = () => {
    setDinnerMealOn(false)
    setGuideWithTip(null)
    setErrorText((prev) => (prev === GUIDE_TIP_REQUIRED_ERROR ? "" : prev))
  }

  const handleConfirm = async () => {
    const adult = Math.max(0, Number(adultCount || 0))
    const child = Math.max(0, Number(childrenCount || 0))
    const foc = Math.max(0, Number(focCount || 0))
    if (!agencyId || !agentId) {
      setErrorText("Agency and Agent are required.")
      return
    }
    if (!keepSameDates && !startDate) {
      setErrorText("Please select a start date.")
      return
    }
    if (changeValues && guideWithTip === null) {
      setErrorText(GUIDE_TIP_REQUIRED_ERROR)
      return
    }
    setErrorText("")
    await onConfirm({
      agency_id: agencyId,
      agent_id: agentId,
      foc_pax_number: foc,
      number_of_adults: adult,
      number_of_children: child,
      ...(keepSameDates ? {} : { start_date: startDate }),
      ...(changeValues
        ? { flag_guide_with_tip: Boolean(guideWithTip), flag_dinner_meal: dinnerMealOn }
        : {}),
    })
  }

  return (
    <Popup
      isOpen={isOpen}
      onClose={onClose}
      title="Duplicate Quotation"
      primaryButton={{ label: isSubmitting ? "Duplicating..." : "Duplicate Quote", onClick: handleConfirm, disabled: isSubmitting }}
      heightFiexd={true}
    >
      <div className={styles.body}>
        <p className={styles.subTitle}>Create a new quotation using this as a base</p>

        <div className={styles.row2}>
          <div>
            <Select
              label="Agency *"
              value={agencyId}
              onChange={(e) => {
                setAgencyId(e.target.value)
                setAgentId("")
              }}
              options={agencySelectOptions}
              placeholder="Select Agency"
            />
          </div>
          <div>
            <Select
              label="Agent *"
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              options={agentSelectOptions}
              placeholder="Select Agent"
            />
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>Passenger Details</div>
          <div className={styles.passengerGrid}>
            <div>
              <div className={styles.fieldLabel}>Adults</div>
              <input
                className={styles.numberInput}
                type="number"
                min={0}
                value={adultCount}
                onChange={(e) => setAdultCount(String(Math.max(0, Number(e.target.value || 0))))}
              />
            </div>
            <div>
              <div className={styles.fieldLabel}>Children</div>
              <input
                className={styles.numberInput}
                type="number"
                min={0}
                value={childrenCount}
                onChange={(e) => setChildrenCount(String(Math.max(0, Number(e.target.value || 0))))}
              />
            </div>
            <div>
              <div className={styles.fieldLabel}>FOC</div>
              <input
                className={styles.numberInput}
                type="number"
                min={0}
                value={focCount}
                onChange={(e) => setFocCount(String(Math.max(0, Number(e.target.value || 0))))}
              />
            </div>
          </div>
          <div className={styles.hint}>Pricing will be recalculated automatically based on passenger changes</div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>Travel Dates</div>
          <div className={styles.radioGroup}>
            <label className={styles.radioRow}>
              <input type="radio" checked={keepSameDates} onChange={() => setKeepSameDates(true)} />
              Keep same travel dates
            </label>
            <label className={styles.radioRow}>
              <input type="radio" checked={!keepSameDates} onChange={() => setKeepSameDates(false)} />
              Change start date
            </label>
          </div>
          {!keepSameDates && (
            <div className={styles.dateInputWrap}>
              <DatePicker
                label=""
                value={startDate}
                onChange={(value) => setStartDate(value)}
              />
              <div className={styles.hint}>End date will be adjusted automatically based on trip duration</div>
            </div>
          )}
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>Trip Duration</div>
          <div className={styles.durationBox}>{tripDurationLabel}</div>
          <div className={styles.hint}>To modify trip duration, edit the quotation after duplication</div>
        </div>

        <div className={styles.section}>
          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={changeValues}
              onChange={(e) => {
                const checked = e.target.checked
                setChangeValues(checked)
                resetMealGuideEdits()
              }}
            />
            Do you want to change the values?
          </label>
          <div className={`${styles.prefsCard} ${changeValues ? styles.prefsCardActive : ""}`}>
            <div className={styles.prefsCardHeading}>
              {changeValues ? "MEAL & GUIDE PREFERENCES" : "SOURCE QUOTATION VALUES"}
            </div>
            <div className={styles.prefsCardRow}>
              <label className={`${styles.checkRow} ${!changeValues ? styles.controlDisabled : ""}`}>
                <input
                  type="checkbox"
                  checked={displayedDinnerOn}
                  disabled={!changeValues}
                  onChange={(e) => setDinnerMealOn(e.target.checked)}
                />
                Dinner
              </label>
              <div className={`${styles.guideGroup} ${!changeValues ? styles.controlDisabled : ""}`}>
                <span className={styles.guideLabel}>Guide:</span>
                <label className={styles.radioRow}>
                  <input
                    type="radio"
                    name="duplicate-guide-tipping"
                    checked={displayedGuideWithTip === true}
                    disabled={!changeValues}
                    onChange={() => {
                      setGuideWithTip(true)
                      setErrorText((prev) => (prev === GUIDE_TIP_REQUIRED_ERROR ? "" : prev))
                    }}
                  />
                  with Tip
                </label>
                <label className={styles.radioRow}>
                  <input
                    type="radio"
                    name="duplicate-guide-tipping"
                    checked={displayedGuideWithTip === false}
                    disabled={!changeValues}
                    onChange={() => {
                      setGuideWithTip(false)
                      setErrorText((prev) => (prev === GUIDE_TIP_REQUIRED_ERROR ? "" : prev))
                    }}
                  />
                  without Tip
                </label>
              </div>
            </div>
          </div>
        </div>

        {errorText && <div className={styles.errorText}>{errorText}</div>}
      </div>
    </Popup>
  )
}

export default DuplicateQuotationModal
